import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, LinearProgress, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
const FIELDS = [{
  key: 'dailyUser',
  label: 'Per user, per day',
  help: 'Stops one person running up a bill'
}, {
  key: 'dailyCompany',
  label: 'Per company, per day',
  help: 'Stops one account running up a bill'
}, {
  key: 'dailyGlobal',
  label: 'Everyone, per day',
  help: 'The ceiling no company can spend past'
}];

/** Ceilings are clamped server side, so the worst a wrong number here can do is be ignored. */
export function LimitsTab() {
  var _draft$tipLogins, _data$maximums$tipLog, _draft$searchPerMinut, _data$maximums$search, _draft$voiceMinutesUs, _data$maximums$voiceM, _draft$voiceSessionMi, _data$maximums$voiceS;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    data = _useState2[0],
    setData = _useState2[1];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    draft = _useState4[0],
    setDraft = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    status = _useState6[0],
    setStatus = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    busy = _useState8[0],
    setBusy = _useState8[1];
  const load = () => livyJson('/settings').then(d => {
    var _d$settings$proactive, _d$settings$tipLogins, _d$settings$upsell, _d$settings$memory, _d$settings$searchPer, _d$settings$voice, _d$settings$voiceMinu, _d$settings$voiceSess;
    setData(d);
    setDraft(_objectSpread(_objectSpread({}, Object.fromEntries(FIELDS.map(f => {
      var _d$settings$f$key;
      return [f.key, String((_d$settings$f$key = d.settings[f.key]) !== null && _d$settings$f$key !== void 0 ? _d$settings$f$key : '')];
    }))), {}, {
      proactive: String((_d$settings$proactive = d.settings.proactive) !== null && _d$settings$proactive !== void 0 ? _d$settings$proactive : 0),
      tipLogins: String((_d$settings$tipLogins = d.settings.tipLogins) !== null && _d$settings$tipLogins !== void 0 ? _d$settings$tipLogins : 3),
      upsell: String((_d$settings$upsell = d.settings.upsell) !== null && _d$settings$upsell !== void 0 ? _d$settings$upsell : 0),
      memory: String((_d$settings$memory = d.settings.memory) !== null && _d$settings$memory !== void 0 ? _d$settings$memory : 0),
      searchPerMinute: String((_d$settings$searchPer = d.settings.searchPerMinute) !== null && _d$settings$searchPer !== void 0 ? _d$settings$searchPer : 120),
      voice: String((_d$settings$voice = d.settings.voice) !== null && _d$settings$voice !== void 0 ? _d$settings$voice : 0),
      voiceMinutesUser: String((_d$settings$voiceMinu = d.settings.voiceMinutesUser) !== null && _d$settings$voiceMinu !== void 0 ? _d$settings$voiceMinu : 30),
      voiceSessionMinutes: String((_d$settings$voiceSess = d.settings.voiceSessionMinutes) !== null && _d$settings$voiceSess !== void 0 ? _d$settings$voiceSess : 10)
    }));
  }).catch(e => setStatus({
    kind: 'error',
    text: e.message
  }));
  useEffect(() => {
    void load();
  }, []);
  const save = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const body = _objectSpread(_objectSpread({}, Object.fromEntries(FIELDS.map(f => [f.key, Number(draft[f.key])]))), {}, {
        proactive: Number(draft.proactive),
        tipLogins: Number(draft.tipLogins),
        upsell: Number(draft.upsell),
        memory: Number(draft.memory),
        searchPerMinute: Number(draft.searchPerMinute),
        voice: Number(draft.voice),
        voiceMinutesUser: Number(draft.voiceMinutesUser),
        voiceSessionMinutes: Number(draft.voiceSessionMinutes)
      });
      await livyJson('/settings', {
        method: 'PUT',
        body
      });
      setStatus({
        kind: 'ok',
        text: 'Saved. It applies to the next turn, no deploy needed.'
      });
      await load();
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setBusy(false);
    }
  };
  if (!data) return /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      mt: '16px'
    }
  }, [0, 1, 2].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    height: 56
  })));
  const used = data.spend.global;
  const ceiling = data.settings.dailyGlobal;
  const pct = ceiling ? Math.min(100, used / ceiling * 100) : 0;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      pt: '16px',
      maxWidth: 560
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mb: '18px'
    }
  }, "Spend ceilings for Livy. A change applies to the next turn rather than the next deploy. Each one is clamped in code, so a value above the maximum is reduced rather than accepted."), /*#__PURE__*/React.createElement(Box, {
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '14px',
      mb: '20px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    justifyContent: "space-between",
    sx: {
      mb: '6px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600
    }
  }, "Today, everyone"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontVariantNumeric: 'tabular-nums'
    }
  }, "$", used.toFixed(2), " of $", ceiling)), /*#__PURE__*/React.createElement(LinearProgress, {
    variant: "determinate",
    value: pct,
    color: pct > 80 ? 'warning' : 'primary',
    sx: {
      height: 6,
      borderRadius: 3
    }
  })), status && /*#__PURE__*/React.createElement(Alert, {
    severity: status.kind === 'ok' ? 'success' : 'error',
    sx: {
      mb: '14px'
    }
  }, status.text), /*#__PURE__*/React.createElement(Stack, {
    spacing: "14px"
  }, FIELDS.map(f => {
    var _draft$f$key;
    return /*#__PURE__*/React.createElement(TextField, {
      key: f.key,
      label: f.label,
      size: "small",
      type: "number",
      value: (_draft$f$key = draft[f.key]) !== null && _draft$f$key !== void 0 ? _draft$f$key : '',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        [f.key]: e.target.value
      })),
      helperText: "".concat(f.help, ". Maximum ").concat(data.maximums[f.key], "."),
      InputProps: {
        startAdornment: /*#__PURE__*/React.createElement(Box, {
          sx: {
            mr: '6px',
            color: 'text.secondary'
          }
        }, "$")
      }
    });
  })), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600,
      mt: '24px',
      mb: '6px'
    }
  }, "Proactive help"), /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px"
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.proactive === '1',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        proactive: e.target.checked ? '1' : '0'
      }))
    }),
    label: "Greet people on login with suggested actions"
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Greet and show tips for the first N logins",
    size: "small",
    type: "number",
    value: (_draft$tipLogins = draft.tipLogins) !== null && _draft$tipLogins !== void 0 ? _draft$tipLogins : '',
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      tipLogins: e.target.value
    })),
    helperText: "After this many logins Livy waits to be asked, unless setup is unfinished. Maximum ".concat((_data$maximums$tipLog = data.maximums.tipLogins) !== null && _data$maximums$tipLog !== void 0 ? _data$maximums$tipLog : 10, ".")
  }), /*#__PURE__*/React.createElement(TextField, {
    id: "livy-limit-search-per-minute",
    label: "Searches per person, per minute",
    size: "small",
    type: "number",
    value: (_draft$searchPerMinut = draft.searchPerMinute) !== null && _draft$searchPerMinut !== void 0 ? _draft$searchPerMinut : '',
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      searchPerMinute: e.target.value
    })),
    helperText: "Palette searches and help-article lookups. One keystroke is two. Maximum ".concat((_data$maximums$search = data.maximums.searchPerMinute) !== null && _data$maximums$search !== void 0 ? _data$maximums$search : 600, ".")
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.upsell === '1',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        upsell: e.target.checked ? '1' : '0'
      }))
    }),
    label: "Mention bill automation to Account Holders entering bills by hand (at most once a month)"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.memory === '1',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        memory: e.target.checked ? '1' : '0'
      }))
    }),
    label: "Learn from conversations: Livy remembers how each person works (their properties, preferences, recurring questions); they can see and delete it in Help"
  })), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600,
      mt: '24px',
      mb: '6px'
    }
  }, "Voice calls"), /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px"
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: draft.voice === '1',
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        voice: e.target.checked ? '1' : '0'
      }))
    }),
    label: "Let customers call Livy (voice)"
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Call minutes per person, per day",
    size: "small",
    type: "number",
    value: (_draft$voiceMinutesUs = draft.voiceMinutesUser) !== null && _draft$voiceMinutesUs !== void 0 ? _draft$voiceMinutesUs : '',
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      voiceMinutesUser: e.target.value
    })),
    helperText: "Each call counts its full length when it starts and gives back what it did not use. Maximum ".concat((_data$maximums$voiceM = data.maximums.voiceMinutesUser) !== null && _data$maximums$voiceM !== void 0 ? _data$maximums$voiceM : 120, ".")
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Longest call, in minutes",
    size: "small",
    type: "number",
    value: (_draft$voiceSessionMi = draft.voiceSessionMinutes) !== null && _draft$voiceSessionMi !== void 0 ? _draft$voiceSessionMi : '',
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      voiceSessionMinutes: e.target.value
    })),
    helperText: "The call is hung up at this length. Maximum ".concat((_data$maximums$voiceS = data.maximums.voiceSessionMinutes) !== null && _data$maximums$voiceS !== void 0 ? _data$maximums$voiceS : 20, ".")
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "10px",
    alignItems: "center",
    sx: {
      mt: '16px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: save,
    disabled: busy,
    sx: {
      textTransform: 'none'
    }
  }, "Save settings"), data.settings.updatedAt && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, "Last changed ", new Date(data.settings.updatedAt).toLocaleString())));
}